// Reiner SVG-Renderer: liefert Markup als String.
// Wird für die Anzeige (Farben als CSS-Variablen) und für Exporte
// (feste Farben, helles Theme) gleichermaßen genutzt.
import { DIM, labelWidth } from '../layout/index.js';
import { byId, statusOf, DECISION_COLOR } from '../model.js';

export const LIVE_COLORS = {
  bg: 'var(--pa-bg)',
  laneA: 'var(--pa-lane-a)',
  laneB: 'var(--pa-lane-b)',
  laneStroke: 'var(--pa-lane-stroke)',
  header: 'var(--pa-lane-header)',
  text: 'var(--pa-text)',
  muted: 'var(--pa-text-muted)',
  node: 'var(--pa-node)',
  nodeStroke: 'var(--pa-node-stroke)',
  accent: 'var(--pa-accent)',
  chipBg: 'var(--pa-chip)',
  labelBg: 'var(--pa-bg)',
};

export const LIGHT_COLORS = {
  bg: '#FFFFFF',
  laneA: '#F7FBFD',
  laneB: '#FFFFFF',
  laneStroke: '#CBDDE7',
  header: '#F0F8FC',
  text: '#333333',
  muted: '#666666',
  node: '#FFFFFF',
  nodeStroke: '#9FB3C1',
  accent: '#009CDE',
  chipBg: '#E8EEF2',
  labelBg: '#FFFFFF',
};

const FONT = "'Segoe UI','Segoe UI Web (West European)',Selawik,-apple-system,BlinkMacSystemFont,'Helvetica Neue','Noto Sans',Arial,sans-serif";

export function edgeLabel(data, e) {
  const t = byId(data.edgeTypes, e.typ);
  return [e.label, t && t.kennung].filter(Boolean).join(' · ');
}

export const esc = (s) =>
  String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Einfacher Zeilenumbruch nach geschätzter Zeichenbreite. */
export function wrap(text, maxChars, maxLines) {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = '';
  for (let w of words) {
    while (w.length > maxChars) {
      if (cur) { lines.push(cur); cur = ''; }
      lines.push(w.slice(0, maxChars - 1) + '-');
      w = w.slice(maxChars - 1);
    }
    const next = cur ? cur + ' ' + w : w;
    if (next.length > maxChars) { lines.push(cur); cur = w; }
    else cur = next;
  }
  if (cur) lines.push(cur);
  if (lines.length > maxLines) {
    const cut = lines.slice(0, maxLines);
    cut[maxLines - 1] = cut[maxLines - 1].replace(/.{0,2}$/, '') + '…';
    return cut;
  }
  return lines;
}

function roundedPath(pts, r = 7) {
  if (pts.length < 2) return '';
  let d = `M${pts[0][0]},${pts[0][1]}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const [px, py] = pts[i - 1], [x, y] = pts[i], [nx, ny] = pts[i + 1];
    const d1 = Math.hypot(x - px, y - py), d2 = Math.hypot(nx - x, ny - y);
    const rr = Math.min(r, d1 / 2, d2 / 2);
    const ax = x - ((x - px) / d1) * rr, ay = y - ((y - py) / d1) * rr;
    const bx = x + ((nx - x) / d2) * rr, by = y + ((ny - y) / d2) * rr;
    d += ` L${ax.toFixed(1)},${ay.toFixed(1)} Q${x},${y} ${bx.toFixed(1)},${by.toFixed(1)}`;
  }
  const l = pts[pts.length - 1];
  return d + ` L${l[0]},${l[1]}`;
}

// Safari (und andere Browser) erzwingen eine Mindestschriftgröße. Kleine SVG-Texte würden
// dadurch vergrößert und liefen aus den Kästchen. Deshalb wird jeder Text intern mit
// vierfacher Größe gesetzt und per Transformation verkleinert.
const TF = 4;
export function T(x, y, size, fill, text, { anchor = 'middle', weight = 400, extra = '' } = {}) {
  return `<text transform="translate(${(+x).toFixed(1)},${(+y).toFixed(1)}) scale(${1 / TF})" font-size="${size * TF}" font-weight="${weight}" text-anchor="${anchor}" fill="${fill}"${extra}>${esc(text)}</text>`;
}

/** Geschätzte Textbreite (Segoe UI / SF Pro) in px. */
export const textW = (s, size) => String(s || '').length * size * 0.56;

function textLines(lines, cx, cy, size, color, weight = 400) {
  const lh = size * 1.28;
  const y0 = cy - ((lines.length - 1) * lh) / 2 + size * 0.36;
  return lines.map((l, i) => T(cx, y0 + i * lh, size, color, l, { weight })).join('');
}

/**
 * @param {object} data      Gesamtdaten (für Bahnen, Systeme, Kantentypen, Entscheidungen)
 * @param {object} layout    Ergebnis von layoutProcess
 * @param {object[]} nodes   Knoten des Prozesses
 * @param {object} opts      { colors, selected, zielbild, edit, forExport, kommentare: { [nodeId]: Anzahl Kommentare },
 *                            aufgaben: { [nodeId]: offene Aufgaben } }
 */
export function renderDiagram(data, layout, nodes, opts = {}) {
  const C = opts.colors || LIVE_COLORS;
  const D = DIM;
  const sel = opts.selected || null;
  const parts = [];
  const edgeTypes = data.edgeTypes;

  // Pfeilspitzen je Kantentyp
  parts.push('<defs>');
  for (const t of edgeTypes) {
    parts.push(`<marker id="pa-arrow-${t.id}" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="9" markerHeight="9" markerUnits="userSpaceOnUse" orient="auto"><path d="M0,0 L10,5 L0,10 z" fill="${t.farbe}"/></marker>`);
  }
  parts.push('</defs>');

  // Bahnen
  parts.push('<g class="pa-lanes">');
  layout.lanes.forEach((l, i) => {
    const lane = byId(data.lanes, l.id) || { name: l.id, farbe: '#888' };
    parts.push(`<rect x="0" y="${l.y}" width="${layout.width}" height="${l.h}" fill="${i % 2 ? C.laneB : C.laneA}" stroke="${C.laneStroke}" stroke-width="1"/>`);
    parts.push(`<rect x="0" y="${l.y}" width="${D.laneHeader}" height="${l.h}" fill="${C.header}" stroke="${C.laneStroke}" stroke-width="1"/>`);
    parts.push(`<rect x="0" y="${l.y}" width="6" height="${l.h}" fill="${lane.farbe}"/>`);
    const lines = wrap(lane.name, 17, 3);
    parts.push(textLines(lines, D.laneHeader / 2 + 3, l.y + l.h / 2, 13, C.text, 600));
  });
  parts.push('</g>');

  // Kanten
  parts.push('<g class="pa-edges">');
  for (const e of layout.edges) {
    const t = byId(edgeTypes, e.typ) || edgeTypes[0];
    const d = roundedPath(e.points);
    const isSel = sel && sel.type === 'edge' && sel.id === e.id;
    parts.push(`<g class="pa-edge${isSel ? ' is-selected' : ''}" data-edge="${esc(e.id)}">`);
    parts.push(`<path d="${d}" fill="none" stroke="transparent" stroke-width="14"/>`);
    if (isSel) parts.push(`<path d="${d}" fill="none" stroke="${C.accent}" stroke-opacity="0.35" stroke-width="8"/>`);
    parts.push(`<path d="${d}" fill="none" stroke="${t.farbe}" stroke-width="${t.breite || 1.6}"${t.dash ? ` stroke-dasharray="${t.dash}"` : ''} marker-end="url(#pa-arrow-${t.id})"/>`);
    const label = edgeLabel(data, e);
    if (label) {
      const w = labelWidth(label);
      parts.push(`<rect x="${(e.labelPos.x - w / 2).toFixed(1)}" y="${e.labelPos.y - 10}" width="${w.toFixed(1)}" height="20" rx="10" fill="${C.labelBg}" stroke="${t.farbe}" stroke-width="0.9"/>`);
      parts.push(T(e.labelPos.x, e.labelPos.y + 3.8, 10.5, C.text, label.length > 28 ? label.slice(0, 27) + '…' : label));
    }
    parts.push('</g>');
  }
  parts.push('</g>');

  // Knoten
  parts.push('<g class="pa-nodes">');
  for (const n of nodes) {
    const p = layout.nodes[n.id];
    if (!p) continue;
    parts.push(renderNode(data, n, p, C, opts));
  }
  parts.push('</g>');
  return parts.join('');
}

function renderNode(data, n, p, C, opts) {
  const { x, y, w, h } = p;
  const cx = x + w / 2, cy = y + h / 2;
  const lane = byId(data.lanes, n.lane) || { farbe: '#888' };
  const isSel = opts.selected && opts.selected.type === 'node' && opts.selected.id === n.id;
  const dec = n.decisionId ? byId(data.decisions, n.decisionId) : null;
  const zb = opts.zielbild && (n.ausbaustufe2 || n.nurZielbild);
  const o = [];
  const cls = ['pa-node', 'typ-' + n.typ, isSel ? 'is-selected' : '', zb ? 'is-zielbild' : ''].filter(Boolean).join(' ');
  o.push(`<g class="${cls}" data-node="${esc(n.id)}">`);
  const stroke = isSel ? C.accent : C.nodeStroke;
  const sw = isSel ? 2.6 : 1.2;
  const dashed = n.nurZielbild ? ' stroke-dasharray="5 4"' : '';
  const FOOT = 22; // Fußzeile für Systemkürzel und Symbol
  const box = !['start', 'ende', 'gateway'].includes(n.typ);
  const TS = 13; // Titelgröße
  // Titelbereich: Kasten ohne Fußzeile, links Farbbalken, rechts Status-Punkt
  let title = { cx: x + w / 2 + 2, cy: y + (h - FOOT) / 2 + 3, width: w - 30, lines: 3, size: TS };

  if (n.typ === 'start' || n.typ === 'ende') {
    const ph = 48, py = cy - ph / 2;
    o.push(`<rect x="${x + 6}" y="${py}" width="${w - 12}" height="${ph}" rx="24" fill="${C.node}" stroke="${n.typ === 'start' ? '#2E8B57' : C.text}" stroke-width="${n.typ === 'ende' ? 3 : 2}"${dashed}/>`);
    if (isSel) o.push(`<rect x="${x + 2}" y="${py - 4}" width="${w - 4}" height="${ph + 8}" rx="28" fill="none" stroke="${C.accent}" stroke-width="2"/>`);
    title = { cx, cy, width: w - 40, lines: 2, size: TS };
  } else if (n.typ === 'gateway') {
    const hw = w / 2 - 2, hh = h / 2;
    o.push(`<path d="M${cx - hw},${cy} L${cx},${cy - hh} L${cx + hw},${cy} L${cx},${cy + hh} Z" fill="${C.node}" stroke="${isSel ? C.accent : lane.farbe}" stroke-width="${isSel ? 2.6 : 1.8}"${dashed}/>`);
    title = { cx, cy, width: w * 0.56, lines: 3, size: 12 };
  } else {
    const isDec = n.typ === 'entscheidung';
    const decOpen = isDec && (!dec || dec.status !== 'entschieden');
    o.push(`<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="9" fill="${C.node}" stroke="${isDec ? DECISION_COLOR : stroke}" stroke-width="${isDec ? 2.4 : sw}"${isDec && decOpen ? ' stroke-dasharray="7 4"' : dashed}/>`);
    if (isDec && isSel) o.push(`<rect x="${x - 4}" y="${y - 4}" width="${w + 8}" height="${h + 8}" rx="12" fill="none" stroke="${C.accent}" stroke-width="2"/>`);
    if (n.typ === 'subprozess') o.push(`<rect x="${x + 4}" y="${y + 4}" width="${w - 8}" height="${h - 8}" rx="6" fill="none" stroke="${stroke}" stroke-width="0.9"/>`);
    if (n.typ === 'automatik') o.push(`<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="9" fill="${C.accent}" fill-opacity="0.08"/>`);
    if (isDec) {
      o.push(`<circle cx="${x + 15}" cy="${y + 15}" r="9.5" fill="${DECISION_COLOR}"/>`);
      o.push(T(x + 15, y + 19.5, 12.5, '#fff', decOpen ? '?' : '✓', { weight: 700 }));
      title = { ...title, cx: cx + 8, width: w - 46 };
    } else {
      o.push(`<rect x="${x}" y="${y + 7}" width="4" height="${h - 14}" rx="2" fill="${lane.farbe}"/>`);
    }
    // Fußzeile: Trennlinie, Symbol rechts
    o.push(`<line x1="${x + 10}" y1="${y + h - FOOT}" x2="${x + w - 10}" y2="${y + h - FOOT}" stroke="${C.nodeStroke}" stroke-opacity="0.35" stroke-width="1"/>`);
    const iy = y + h - FOOT / 2;
    if (n.typ === 'subprozess') {
      o.push(`<g data-drill="${esc(n.subprocessId || '')}" class="pa-drill"><rect x="${x + w - 30}" y="${iy - 8}" width="20" height="16" rx="4" fill="${C.accent}" fill-opacity="0.12" stroke="${C.accent}" stroke-width="1"/><path d="M${x + w - 24},${iy} h8 M${x + w - 20},${iy - 4} v8" stroke="${C.accent}" stroke-width="1.6" stroke-linecap="round"/><title>Unterprozess öffnen</title></g>`);
    } else if (n.typ === 'automatik') o.push(gearIcon(x + w - 19, iy, C.accent));
    else if (n.typ === 'beleg') o.push(docIcon(x + w - 25, iy - 7, C.muted));
  }

  const chars = Math.max(8, Math.floor(title.width / (title.size * 0.56)));
  const lines = wrap(n.titel, chars, title.lines);
  o.push(textLines(lines, title.cx, title.cy, title.size, C.text, n.typ === 'start' || n.typ === 'ende' ? 600 : 500));

  // Systemkürzel in der Fußzeile links
  if (box) {
    let sx = x + 11;
    const fy = y + h - FOOT / 2;
    // Verantwortlich in der Fußzeile rechts (vor dem Typsymbol), gekürzt; voller Name beim Überfahren
    const ve = (n.verantwortlich || '').trim();
    let limit = x + w - 36;
    if (ve) {
      const re = ['subprozess', 'automatik', 'beleg'].includes(n.typ) ? x + w - 36 : x + w - 11;
      const kurz = ve.length > 18 ? ve.slice(0, 17) + '…' : ve;
      const vw = textW(kurz, 9.5) + 14;
      o.push(`<g class="pa-verantwortlich"><title>Verantwortlich: ${esc(ve)}</title>${personIcon(re - vw + 5, fy, C.muted)}${T(re, fy + 3.4, 9.5, C.muted, kurz, { anchor: 'end' })}</g>`);
      limit = re - vw - 6;
    }
    const systems = (n.systemIds || []).map((sid) => byId(data.systems, sid)).filter(Boolean);
    // Infotext beim Überfahren: voller Name und Beschreibung des Systems
    const tip = (s) => esc(s.name + (s.beschreibung ? ' – ' + s.beschreibung : ''));
    for (const [i, s] of systems.entries()) {
      const label = s.kurz || s.name;
      const cw = textW(label, 9.5) + 12;
      if (sx + cw > limit) {
        o.push(`<g><title>${systems.slice(i).map(tip).join('\n')}</title>${T(sx + 2, fy + 3.5, 9.5, C.muted, '…', { anchor: 'start' })}</g>`);
        break;
      }
      o.push(`<g class="sys"><title>${tip(s)}</title><rect x="${sx}" y="${fy - 7.5}" width="${cw.toFixed(1)}" height="15" rx="4" fill="${s.farbe}" fill-opacity="0.16"/>`);
      o.push(T(sx + cw / 2, fy + 3.4, 9.5, C.text, label, { weight: 600 }) + '</g>');
      sx += cw + 4;
    }
  }
  // Status-Punkt oben rechts
  const st = statusOf(n.status);
  if (st && n.typ !== 'start' && n.typ !== 'ende') {
    const sx = n.typ === 'gateway' ? cx + 30 : x + w - 11;
    const sy = n.typ === 'gateway' ? cy - 26 : y + 11;
    o.push(`<circle cx="${sx}" cy="${sy}" r="5.5" fill="${st.farbe}"><title>${esc(st.name)}</title></circle>`);
  }
  // Kommentare: Sprechblase mit Zahl oben links (nur in der Oberfläche, nicht in Exporten)
  const offen = opts.kommentare?.[n.id] || 0;
  if (offen > 0) {
    const kx = n.typ === 'gateway' ? cx - 44 : x - 7;
    const ky = n.typ === 'gateway' ? cy - 34 : y - 9;
    const kw = offen > 9 ? 26 : 20;
    o.push(`<g class="pa-kommentare" data-kommentare="${offen}"><title>${offen} offene${offen === 1 ? 'r' : ''} Kommentar${offen === 1 ? '' : 'e'}</title>`);
    o.push(`<path d="M${kx + 4},${ky} h${kw - 8} a4,4 0 0 1 4,4 v8 a4,4 0 0 1 -4,4 h${-(kw - 12)} l-4,4 v-4 a4,4 0 0 1 -4,-4 v-8 a4,4 0 0 1 4,-4 z" fill="${C.accent}"/>`);
    o.push(T(kx + kw / 2, ky + 11.6, 9.5, '#fff', String(offen > 99 ? '99+' : offen), { weight: 700 }));
    o.push('</g>');
  }
  // Offene Aufgaben: Plakette mit Haken und Zahl unten rechts (nur in der Oberfläche, nicht in Exporten)
  const aufg = opts.aufgaben?.[n.id] || 0;
  if (aufg > 0) {
    const txt = aufg > 99 ? '99+' : String(aufg);
    const aw = 22 + txt.length * 6;
    const ax = n.typ === 'gateway' ? cx + 30 : x + w - aw + 7;
    const ay = n.typ === 'gateway' ? cy + 20 : y + h - 9;
    o.push(`<g class="pa-aufgaben" data-aufgaben="${aufg}"><title>${aufg} offene Aufgabe${aufg === 1 ? '' : 'n'}</title>`);
    o.push(`<rect x="${ax}" y="${ay}" width="${aw}" height="17" rx="8.5" fill="#D97706"/>`);
    o.push(`<path d="M${ax + 6},${ay + 8.5} l3,3 l5.5,-6" fill="none" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>`);
    o.push(T(ax + 17 + (txt.length * 6) / 2, ay + 12.2, 9.5, '#fff', txt, { weight: 700 }));
    o.push('</g>');
  }
  // Ausbaustufe 2: Plakette am oberen Rand
  if (n.ausbaustufe2 || n.nurZielbild) {
    const bx = n.typ === 'gateway' ? cx - 18 : x + w - 52;
    const by = n.typ === 'gateway' ? y - 10 : y - 9;
    o.push(`<rect x="${bx}" y="${by}" width="36" height="17" rx="8.5" fill="${zb ? C.accent : C.node}" stroke="${C.accent}" stroke-width="1.2"/>`);
    o.push(T(bx + 18, by + 12.2, 9.5, zb ? '#fff' : C.accent, 'AS 2', { weight: 700 }));
  }
  if (opts.edit) {
    o.push(`<g class="pa-port" data-port="${esc(n.id)}"><circle cx="${x + w + 1}" cy="${cy}" r="10" fill="${C.accent}" fill-opacity="0.18"/><circle cx="${x + w + 1}" cy="${cy}" r="5.5" fill="${C.accent}"/></g>`);
  }
  o.push('</g>');
  return o.join('');
}

function personIcon(x, y, color) {
  return `<g transform="translate(${x},${y})"><circle cy="-2.6" r="2.2" fill="${color}"/><path d="M-3.8,4.4 a3.8,3.6 0 0 1 7.6,0 z" fill="${color}"/></g>`;
}
function gearIcon(x, y, color) {
  return `<g transform="translate(${x},${y})" opacity="0.85"><circle r="4.2" fill="none" stroke="${color}" stroke-width="1.6"/><path d="M0,-7 V-4.6 M0,7 V4.6 M-7,0 H-4.6 M7,0 H4.6 M-5,-5 L-3.3,-3.3 M5,5 L3.3,3.3 M-5,5 L-3.3,3.3 M5,-5 L3.3,-3.3" stroke="${color}" stroke-width="1.6" stroke-linecap="round"/></g>`;
}
function docIcon(x, y, color) {
  return `<path transform="translate(${x},${y})" d="M0,0 h8 l5,5 v10 h-13 z M8,0 v5 h5" fill="none" stroke="${color}" stroke-width="1.2" stroke-linejoin="round"/>`;
}

/** Komplettes, eigenständiges SVG inkl. Kopfzeile (für Export und Druck). */
export function standaloneSvg(data, layout, nodes, { title, subtitle, logos = [], compact = false } = {}) {
  const C = LIGHT_COLORS;
  const head = compact ? 4 : 64;
  const pad = 16;
  const W = layout.width + pad * 2;
  const H = layout.height + head + pad * 2 + 30;
  const p = [];
  p.push(`<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" font-family="${esc(FONT)}">`);
  p.push(`<rect width="${W}" height="${H}" fill="#FFFFFF"/>`);
  if (title) p.push(T(pad, 30, 20, '#009CDE', title, { anchor: 'start' }));
  if (subtitle) p.push(T(pad, 50, 11.5, '#666666', subtitle, { anchor: 'start' }));
  let lx = W - pad;
  for (const lg of compact ? [] : logos) {
    if (!lg || !lg.src) continue;
    const lh = 34, lw = lh * (lg.ratio || 2);
    lx -= lw;
    p.push(`<image href="${lg.src}" x="${lx}" y="${(head - lh) / 2}" width="${lw}" height="${lh}" preserveAspectRatio="xMidYMid meet"/>`);
    lx -= 18;
  }
  p.push(`<g transform="translate(${pad},${head})">${renderDiagram(data, layout, nodes, { colors: C, forExport: true })}</g>`);
  // Legende Kantentypen
  let x = pad;
  const ly = head + layout.height + 24;
  for (const t of data.edgeTypes) {
    p.push(`<line x1="${x}" y1="${ly}" x2="${x + 26}" y2="${ly}" stroke="${t.farbe}" stroke-width="${t.breite || 1.6}"${t.dash ? ` stroke-dasharray="${t.dash}"` : ''}/>`);
    p.push(T(x + 32, ly + 4, 10.5, '#333333', t.name, { anchor: 'start' }));
    x += 48 + textW(t.name, 10.5);
  }
  p.push('</svg>');
  return { svg: p.join(''), width: W, height: H };
}

export const FONT_STACK = FONT;
